// "Merge into…" for the customer page (QA CU1-005, owner decision C2): pick
// the customer to keep, preview what moves, then merge. Irreversible — this
// customer's bills, points, store credit, vouchers, communications and
// feedback move to the target and this customer is deactivated.

import { useEffect, useState } from 'react';
import { Badge, Button, Input, Modal } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { ApiError } from '@/lib/api-client';
import { useCustomerList, useCustomerMergePreview, useMergeCustomer } from './use-customer-query';
import type { Customer } from './types';

interface MergeCustomerModalProps {
  open: boolean;
  onClose: () => void;
  customer: Pick<Customer, 'id' | 'name' | 'phone'>;
  /** Called with the kept customer's id once the merge succeeded. */
  onMerged: (targetId: string) => void;
}

export function MergeCustomerModal({ open, onClose, customer, onMerged }: MergeCustomerModalProps) {
  const currency = useTenantCurrency();
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [target, setTarget] = useState<Pick<Customer, 'id' | 'name' | 'phone'> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setSearch(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const listQ = useCustomerList({ search: search || undefined, limit: 8 });
  const candidates = search ? (listQ.data?.data ?? []).filter((c) => c.id !== customer.id) : [];
  const previewQ = useCustomerMergePreview(customer.id, target?.id);
  const merge = useMergeCustomer(customer.id);
  const preview = previewQ.data;
  const refused = (preview?.refusals.length ?? 0) > 0;

  async function handleMerge() {
    if (!target) return;
    setError(null);
    try {
      await merge.mutateAsync(target.id);
      onMerged(target.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Please try again.');
    }
  }

  const rows: { label: string; value: string }[] = preview
    ? [
        { label: 'Bills', value: String(preview.moving.bills) },
        { label: 'Loyalty points', value: preview.moving.loyalty_points.toLocaleString() },
        { label: 'Points history entries', value: String(preview.moving.loyalty_transactions) },
        { label: 'Store credit available', value: formatPaise(preview.moving.store_credit_available, currency) },
        { label: 'Credit notes', value: String(preview.moving.store_credits) },
        { label: 'Memberships', value: String(preview.moving.memberships) },
        { label: 'Voucher redemptions', value: String(preview.moving.voucher_redemptions) },
        { label: 'Messages sent', value: String(preview.moving.communications) },
        { label: 'Feedback', value: String(preview.moving.feedback) },
      ]
    : [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Merge into another customer"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleMerge}
            disabled={!preview || refused || merge.isPending}
            loading={merge.isPending}
          >
            Merge and deactivate {customer.name}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-900/30 dark:to-violet-900/30 border border-indigo-100 dark:border-indigo-800 p-3 text-sm text-neutral-700 dark:text-neutral-200">
          Everything <strong>{customer.name}</strong> ({customer.phone}) owns moves to the customer you pick, and{' '}
          {customer.name} is deactivated. This cannot be undone.
        </div>

        {target ? (
          <div className="flex items-center justify-between rounded-lg border border-neutral-200 dark:border-neutral-700 p-3">
            <div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">Keep this customer</p>
              <p className="font-medium text-neutral-900 dark:text-white">
                {target.name} <span className="text-neutral-500 dark:text-neutral-400">{target.phone}</span>
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setTarget(null)}>
              Change
            </Button>
          </div>
        ) : (
          <div>
            <Input
              label="Customer to keep"
              placeholder="Search by name, phone or email"
              value={query}
              onChange={setQuery}
            />
            {candidates.length > 0 ? (
              <ul className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-neutral-200 dark:border-neutral-700 divide-y divide-neutral-100 dark:divide-neutral-700">
                {candidates.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setTarget({ id: c.id, name: c.name, phone: c.phone })}
                      className="w-full text-left px-3 py-2 hover:bg-brand-50 dark:hover:bg-neutral-800 transition-all duration-200"
                    >
                      <span className="font-medium text-neutral-900 dark:text-white">{c.name}</span>{' '}
                      <span className="text-sm text-neutral-500 dark:text-neutral-400">{c.phone}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : search && !listQ.isFetching ? (
              <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">No other customer matches.</p>
            ) : null}
          </div>
        )}

        {target && previewQ.isLoading ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">Loading preview…</p>
        ) : null}

        {preview ? (
          <div className="space-y-3">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-neutral-500 dark:text-neutral-400">
                  <th className="py-1 font-medium">Moves to {preview.target.name}</th>
                  <th className="py-1 font-medium text-right">From {preview.source.name}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.label} className="even:bg-neutral-50 dark:even:bg-neutral-800/50">
                    <td className="py-1.5 px-1 text-neutral-700 dark:text-neutral-200">{r.label}</td>
                    <td className="py-1.5 px-1 text-right tabular-nums text-neutral-900 dark:text-white">{r.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {refused ? (
              <div className="space-y-1">
                {preview.refusals.map((r) => (
                  <div key={r.code} className="flex items-start gap-2 text-sm text-danger-700 dark:text-danger-300">
                    <Badge variant="danger" size="sm">Can't merge</Badge>
                    <span>{r.message}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {error ? <p className="text-sm text-danger-600 dark:text-danger-400">{error}</p> : null}
      </div>
    </Modal>
  );
}
